"use client";

import { Box, Typography } from "@mui/material";
import NextLink from "next/link";
import { simpleDateFormatter } from "@/src/utils/dateFormatter";
import { isValidDate } from "@/src/utils/is-valid-date";
import {
  doubleRule,
  FONT_DISPLAY,
  FONT_TEXT,
  FONT_UI,
  hairlineTop,
  HEADING_SIZE,
  SHEET_GUTTER,
} from "@/src/components/common/broadsheet/tokens";
import CitationEntry from "@/src/components/common/citation-list/citation-entry";
import Kicker from "@/src/components/common/broadsheet/kicker";
import MetaLine from "@/src/components/common/broadsheet/meta-line";

const DOI_URL =
  process.env.NEXT_PUBLIC_DOI_WEBSITE_URL ?? "https://doi.org/";

export default function CitationForwardLinksPage({
  citation,
  articleHref,
  title,
  description,
}: {
  citation: any;
  articleHref: string;
  title: string;
  description: string;
}) {
  const entries = citation?.data?.forwardLinks ?? [];
  const count = citation?.count ?? entries.length;
  const updated = citation?.updated_at;

  const dateStr =
    updated && isValidDate(new Date(updated))
      ? simpleDateFormatter(updated, { year: "numeric", month: "long", day: "2-digit" })
      : null;

  return (
    <Box sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}>
      {/* Masthead */}
      <Box
        sx={{
          pt: { xs: 1, md: 1.5 },
          pb: { xs: 0.75, md: 1 },
          ...doubleRule,
        }}
      >
        <Kicker sx={{ mb: 0.25 }}>Citation data</Kicker>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { lg: "flex-end" },
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: HEADING_SIZE.page,
                lineHeight: 1.05,
                letterSpacing: "0.01em",
                color: "custom.ink",
              }}
            >
              {title}
            </Typography>
            <Typography
              component="p"
              sx={{
                m: 0,
                mt: 0.25,
                fontFamily: FONT_TEXT,
                fontSize: { xs: "0.9375rem", md: "1rem" },
                lineHeight: 1.6,
                color: "custom.inkMuted",
                maxWidth: "56ch",
              }}
            >
              {description}
            </Typography>
          </Box>
          <MetaLine
            sx={{ flex: "none" }}
            items={[
              `${count} ${count === 1 ? "result" : "results"}`,
              dateStr ? `Updated ${dateStr}` : null,
            ].filter(Boolean) as string[]}
          />
        </Box>
      </Box>

      {/* Back link */}
      <Box
        sx={{
          py: 0.75,
          ...hairlineTop,
        }}
      >
        <Box
          component={NextLink}
          href={articleHref}
          sx={{
            fontFamily: FONT_UI,
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "custom.link",
            textDecoration: "none",
            paddingBlock: "0.4rem",
            "&:hover": {
              color: "custom.linkHover",
              textDecoration: "underline",
              textUnderlineOffset: "0.15em",
            },
          }}
        >
          ← Back to article
        </Box>
      </Box>

      {/* Entries */}
      <Box sx={{ pt: { xs: 1, md: 1.25 } }}>
        {entries.length === 0 ? (
          <Typography
            component="p"
            sx={{
              fontFamily: FONT_UI,
              fontSize: "0.8125rem",
              color: "custom.inkMuted",
            }}
          >
            No results found.
          </Typography>
        ) : (
          entries.map((item: any, index: number) => {
            const entryTitle = item?.article?.title ?? "Untitled";
            const doi = item?.doi;
            const href = doi
              ? `${DOI_URL}${doi}`
              : "#";
            const journal = item?.journal?.title;
            const year = item?.year;
            const volume = item?.volume;
            const issue = item?.issue;
            const authors = authorStr(item?.contributors);

            return (
              <CitationEntry
                key={index}
                index={index}
                title={entryTitle}
                href={href}
                journal={
                  journal
                    ? `${journal}${volume ? ` ${volume}` : ""}${issue ? `(${issue})` : ""}${year ? ` (${year})` : ""}`
                    : undefined
                }
                authors={authors}
              />
            );
          })
        )}
      </Box>
    </Box>
  );
}

function authorStr(contributors?: any[]) {
  if (!contributors?.length) return null;
  const names = contributors
    .filter((c) => c.givenName || c.surname)
    .map((c) => `${c.givenName ?? ""} ${c.surname ?? ""}`.trim())
    .filter(Boolean);
  return names.length > 0 ? names.join("; ") : null;
}
